import {identity} from 'ramda';
import Decimal, {DecimalSource} from './break_infinity.js';
import {DynamicValue} from './interfaces/dynamic-value.js';
import {IValuable} from './interfaces/valuable.js';
import {Resource} from './resource.js';
import {fancy, formatDelta, formatEng} from './utils.js';

export class Modifier {
  value: (val: Decimal) => Decimal = (val) => new Decimal(val);
  string = () => 'Tells you nothing';
  constructor(value: (val: Decimal) => Decimal, string: () => string) {
    this.value = value;
    this.string = string;
  }
}

export const Mods = {
  plusPer: (value: DecimalSource, resource: Resource) =>
    new Modifier(
      (val: Decimal) => val.plus(resource.value.times(value)),
      () =>
        `+ ${formatEng(resource.value.times(value))} [${value} × ${fancy(
          resource.name,
          resource.displayName
        )}]`
    ),
  plusLevel: (resource: Resource) =>
    new Modifier(
      (val: Decimal) => val.plus(resource.level),
      () =>
        `+ ${resource.level} [${fancy(
          resource.name,
          resource.displayName
        )} level]`
    ),
  timesPowLevel: (
    base: DecimalSource,
    resource: Resource,
    levelMap: (level: Decimal) => Decimal = identity,
    levelString = 'level'
  ) => {
    const mappedLevel: () => Decimal = () => levelMap(resource.level);
    return new Modifier(
      (val: Decimal) => val.times(new Decimal(base).pow(mappedLevel())),
      () =>
        `× ${formatEng(
          new Decimal(base).pow(mappedLevel())
        )} [${base} ^ ${mappedLevel()} (${fancy(
          resource.name,
          resource.displayName
        )} ${levelString})]`
    );
  },
  timesLog10: (valuable: IValuable) =>
    new Modifier(
      (val: Decimal) => val.times(1 + valuable.value.max(1).log10()),
      () =>
        `× ${formatEng(
          new Decimal(1 + valuable.value.max(1).log10())
        )} [1 + log_10(${valuable.valueHTML}(min 1))]`
    ),
  timesLog2: (valuable: IValuable) =>
    new Modifier(
      (val: Decimal) => val.times(1 + valuable.value.max(1).log(2)),
      () =>
        `× ${formatEng(
          new Decimal(1 + valuable.value.max(1).log(2))
        )} [1 + log_2(${valuable.valueHTML}(min 1))]`
    ),
  floor: () =>
    new Modifier(
      (val: Decimal) => val.floor(),
      () => `Round down`
    ),
  ceil: () =>
    new Modifier(
      (val: Decimal) => val.ceil(),
      () => `Round up`
    ),
};
